// Module 2 — CRM constants.
//
// CANONICAL vs DISPLAY (WR-009). The database stores a trade-neutral value; the tenant reads
// their own word. `draft` is the canonical status of an account that has no account number
// yet — The Ride's word for that is "Lead" (Val, 2026-07-15). A law firm would say "Prospect".
// Code branches on the canonical value; only the label changes. Never match on the label.
//
// When the per-brand vocabulary system lands with the Scheduler rebuild (see
// Docs/Module_2_Seams_And_PlugIn_Points_v1_00.md §4), these labels move to the database and
// this file becomes the fallback. Until then, one place to change them.

import { resolvePaletteTextHex } from '../../config/paletteContrast';

export const ACCOUNT_STATUS_META = {
    // canonical  label        semantics (Module 1 palette: color means something)
    draft:     { label: 'Lead',      tone: 'open',    hint: 'No account number until it converts' },
    active:    { label: 'Active',    tone: 'good',    hint: 'Live account' },
    suspended: { label: 'Suspended', tone: 'human',   hint: 'Suspended by a person' },
    archived:  { label: 'Archived',  tone: 'muted',   hint: 'Closed / retained' },
};

export const statusMeta = (status) =>
    ACCOUNT_STATUS_META[status] || { label: status || '—', tone: 'muted', hint: '' };

// Tone -> a ROLE, never a colour. Components name the role; the value resolves in
// global.css. This file used to hardcode hexes — including the brand accent — which meant
// a Member pill could never answer to a brand switch. See global.css for the three kinds
// of colour and why only the accent follows the brand.
//
//   system -> brand-*        the ACCENT. Swaps per brand at runtime (applyBrand).
//   good/open/human/link     SEMANTIC roles from the locked Flow palette. Constant across
//                            brands by design: "positive" is green for everyone.
//
// Outlined, not filled — matching the canonical Peacock render (Val's mockups, 2026-07-15):
// a hairline ring in the role's colour with the label in the same colour, on a transparent
// ground. Reads as a tag rather than a highlighter, which is what lets several sit together
// without shouting.
//
// WRITTEN OUT IN FULL, ON PURPOSE. A helper like outline(v) => `border-[${v}]` works today
// (the Play CDN scans the rendered DOM) but would silently drop these classes the moment the
// app moves to Tailwind via PostCSS — the palette doc says that migration is coming, and a
// static scanner can only find class names that literally appear in the source. Interpolated
// class names are a trap that pays out later, in the worst way: no error, just no colour.
export const TONE_CLASS = {
    good:   'border bg-transparent',
    open:   'border bg-transparent',
    alert:  'border bg-transparent',
    gold:   'border bg-transparent',
    human:  'border bg-transparent',
    system: 'border bg-transparent',
    muted:  'border bg-transparent',
    border: 'border bg-transparent',
};

const TONE_COLOR_VAR = {
    excellent: '--ui-excellent',
    good: '--ui-positive',
    caution: '--ui-gold',
    moderate: '--ui-moderate',
    critical: '--ui-alert',
    neutral: '--ui-neutral',
    border: '--ui-border',
    open: '--ui-gold',
    alert: '--ui-alert',
    gold: '--ui-gold',
    human: '--rule-magenta',
    system: '--brand-500',
    muted: '--ink-muted'
};

function readCssHex(variableName) {
    if (typeof window === 'undefined') return null;
    const value = getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
    if (!value) return null;
    if (value.startsWith('#')) return value;
    if (/^\d+\s+\d+\s+\d+$/.test(value)) {
        const [r, g, b] = value.split(/\s+/).map(Number);
        return `#${[r, g, b].map((channel) => channel.toString(16).padStart(2, '0')).join('').toUpperCase()}`;
    }
    return null;
}

export function toneTextStyle(tone) {
    const baseHex = readCssHex(TONE_COLOR_VAR[tone] || '--ink-muted');
    const safeHex = resolvePaletteTextHex(baseHex, {
        theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
        entityId: localStorage.getItem('intelAgentActiveBrand') || null,
        tenantName: localStorage.getItem('intelAgentTenantName') || ''
    });
    return {
        color: safeHex || undefined,
        borderColor: safeHex || undefined
    };
}

export const hasHumanReview = (account) =>
    Boolean(account?.requires_human_review || account?.primary_contact?.requires_human_review);

// Tier pills key off FAMILY, never rank and never the name (migration 020).
//
// Rank was wrong: it meant "rank >= 3 is special", so inserting any tier at rank 3 would
// silently paint it gold. The name is wrong too — the tenant owns it. `family` is the
// canonical value (customer | member | vip), so renaming "VIP · Black" to "Platinum Elite"
// keeps it gold, keeps it a VIP, and keeps it at its rung.
//
// Gold = the VIP family, per the canonical render. All three VIP levels share it; the LEVEL
// is carried by the label (VIP · Black / Diamond / Black Diamond), not by a third colour —
// three golds would read as three unrelated things rather than one ladder.
export const tierTone = (family) => {
    switch (family) {
        case 'vip':    return 'gold';    // the top family — Black · Diamond · Black Diamond
        case 'member': return 'system';  // the accent — follows the brand
        default:       return 'muted';   // customer / base
    }
};

export const paymentStatusTone = (status) => {
    switch (status) {
        case 'paid': return 'good';
        case 'unpaid': return 'open';
        case 'partially_paid': return 'gold';
        case 'charged_off': return 'alert';
        default: return 'muted';
    }
};

// Flag chips on the identity band. Each is a boolean column on accounts.
//
// `tone` is the ON tone. OFF renders muted with "· off" — the canonical render shows
// "Senior · off" rather than hiding it, and that is the better call: a hidden flag and a
// flag that is switched off look identical, so you cannot tell "this client is not a senior"
// from "nobody has said". Showing both states makes the card answer the question.
//
// requires_human_review keeps its own tone: it is a caution (BL-118), not a property.
export const ACCOUNT_FLAGS = [
    { key: 'requires_human_review', label: 'Needs review', tone: 'open',   isActive: (account) => hasHumanReview(account) }, // BL-118 — gold, it wants attention
    { key: 'card_on_file',          label: 'Card on file', tone: 'system'  },
    { key: 'senior',                label: 'Senior',       tone: 'system'  },
    // (Business / Personal is the Account type now - a select, not a flag)
];

export const VIEW_MODES = ['List', 'Card', 'Hybrid'];

// Default view = each user's last-used, remembered (UX Addendum, Gate 3).
export const VIEW_STORAGE_KEY = 'crm.clients.viewMode';

// The one embed every Clients query needs. Two FKs run between accounts and contacts
// (accounts.primary_contact_id and contacts.primary_account_id), so PostgREST cannot guess
// which to follow — the !constraint_name is required, not decoration. Verified live 2026-07-15.
export const ACCOUNT_SELECT = `
    id, org_id, account_number, account_name, status, account_type, entity_id, owner_id,
    is_business, account_kind, card_on_file, senior, requires_human_review, preferred_drivers,
    validated_referral_count, customer_tier_id, primary_contact_id, created_at,
    primary_contact:contacts!accounts_primary_contact_id_fkey (
        id, org_id, name, position, photo_url, emails, phones, notes, requires_human_review,
        sms_transactional_consent, sms_marketing_consent, sms_consent_source, sms_consent_at,
        sms_consent_text, sms_consent_by, sms_customer_agreed_verbally, sms_opted_out_at,
        communication_preferences, sms_consent_note, communication_preferences_set_by, communication_preferences_set_at,
        email_opted_out_at, tags, source, time_zone, time_zone_source,
        contact_methods ( id, kind, label, value, is_primary ),
        contact_accounts!contact_accounts_contact_id_fkey (
            account_id, role,
            account:accounts!contact_accounts_account_id_fkey ( id, account_name, account_kind, account_number, status )
        )
    ),
    tier:tier_profiles!accounts_customer_tier_id_fkey ( id, name, rank, family ),
    opportunities:opportunities!opportunities_account_id_fkey (
        id, value, status, expected_close,
        stage:pipeline_stages!opportunities_stage_id_fkey ( name, type )
    )
`;

// The deal a card should show. An account can carry several; the one worth a line is the
// live one. If a tenant has several open, show the count and the total rather than picking a
// favourite — a card that silently shows "a" deal when there are three is worse than one
// that says three.
//
// If the tenant lacks crm.pipeline, opportunities' RLS returns nothing and this yields null,
// so the deal line disappears on its own. The gate needs no help from the UI.
export const openDeals = (account) =>
    (account.opportunities || []).filter(o => o.status === 'open');

export const dealSummary = (account) => {
    const open = openDeals(account);
    if (!open.length) return null;
    const total = open.reduce((sum, o) => sum + (Number(o.value) || 0), 0);
    return {
        count: open.length,
        total,
        stage: open.length === 1 ? open[0].stage : null,   // one deal = name its stage
        expected: open.length === 1 ? open[0].expected_close : null,
    };
};

export const money = (n) =>
    n == null ? null : new Intl.NumberFormat('en-US', {
        style: 'currency', currency: 'USD', maximumFractionDigits: 0,
    }).format(n);

// Contact reach helpers — emails/phones are TEXT[] on contacts (015).
export const firstEmail = (contact) => contact?.emails?.[0] || null;
export const firstPhone = (contact) => {
    const phone = contact?.phones?.[0];
    return typeof phone === 'string' ? phone : phone?.number || phone?.phone || phone?.value || phone?.phone_number || null;
};

const SETTLED_INVOICE_STATES = new Set(['paid', 'closed']);

function normalizeCardText(value) {
    return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';
}

function truncateCardText(value, limit = 120) {
    const normalized = normalizeCardText(value);
    if (!normalized) return null;
    if (normalized.length <= limit) return normalized;
    return `${normalized.slice(0, limit - 1).trimEnd()}…`;
}

function invoiceState(invoice) {
    return String(invoice?.lifecycle_status || invoice?.status || '').trim().toLowerCase();
}

function invoiceActivityAt(invoice) {
    if (!invoice) return null;
    const state = invoiceState(invoice);
    if (SETTLED_INVOICE_STATES.has(state)) {
        return invoice.payment_date || invoice.updated_at || invoice.created_at || invoice.due_date || null;
    }
    return invoice.created_at || invoice.updated_at || invoice.due_date || invoice.payment_date || null;
}

function invoiceAmount(invoice) {
    const raw = invoice?.final_amount ?? invoice?.total;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
}

export function formatCardDate(dateString) {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function getCardBriefing(account) {
    const note = truncateCardText(account?.primary_contact?.notes, 140);
    return note ? { label: 'Briefing', text: note } : null;
}

export function getRecentCardActivity(account) {
    const invoices = Array.isArray(account?.invoices) ? account.invoices : [];
    const recentInvoice = invoices
        .filter(Boolean)
        .map(invoice => ({ invoice, activityAt: invoiceActivityAt(invoice) }))
        .sort((a, b) => {
            const aTime = a.activityAt ? new Date(a.activityAt).getTime() : 0;
            const bTime = b.activityAt ? new Date(b.activityAt).getTime() : 0;
            return bTime - aTime;
        })[0]?.invoice;

    if (recentInvoice) {
        const state = invoiceState(recentInvoice);
        const amount = money(invoiceAmount(recentInvoice));
        const label = SETTLED_INVOICE_STATES.has(state) ? 'Invoice paid' : 'Invoice created';
        const detail = [recentInvoice.invoice_number, amount].filter(Boolean).join(' · ');
        return {
            kind: 'invoice',
            label,
            detail: detail || 'Recent invoice',
            date: formatCardDate(invoiceActivityAt(recentInvoice)),
            tone: SETTLED_INVOICE_STATES.has(state) ? 'good' : 'system',
        };
    }

    const briefing = getCardBriefing(account);
    if (!briefing) return null;

    return {
        kind: 'communication',
        label: 'Last communication',
        detail: briefing.text,
        date: null,
        tone: 'human',
    };
}
